// pages/TanachBookList.js
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';

export default function TanachBookList() {
  const [books, setBooks] = useState([]);
  const [discipline, setDiscipline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const disciplinesRes = await fetchWithAuth('/api/disciplines/');
        if (!disciplinesRes.ok) throw new Error('Failed');
        const disciplines = await disciplinesRes.json();
        const discipline = disciplines.find(d => d.name.includes('תנ"ך'));

        if (discipline) {
          if (!ignore) setDiscipline(discipline);
          const res = await fetchWithAuth(`/api/disciplines/${discipline.id}/tanach-books/`);
          if (res.ok) {
            const data = await res.json();
            if (!ignore) setBooks(data.filter(b => b.is_enabled));
          }
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת הספרים');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  return (
    <div className="page-wrapper">
      <Breadcrumbs items={[{ to: '/disciplines', label: 'תחומי דעת' }, { label: 'תנ"ך' }]} />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          {discipline?.name || 'תנ"ך'}
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to="/disciplines"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה לתחומי דעת
        </Link>
      </p>

      {/* Email for submitting materials */}
      <div className="max-w-2xl mx-auto mb-8 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
        <p className="text-center text-secondary-700 dark:text-secondary-300 mb-2">
          יש לכם חומרים נוספים לשיתוף?
        </p>
        <p className="text-center">
          <a
            href="mailto:haifakodesh@gmail.com?subject=שיתוף חומרים - תנ&quot;ך"
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium underline"
          >
            haifakodesh@gmail.com
          </a>
        </p>
      </div>

      {loading && <SkeletonCardGrid count={6} />}

      {error && (
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="grid-autofit">
          {books.map(b => (
            <article
              key={b.id}
              className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1"
            >
              <Link
                to={`/tanach/books/${b.id}/chapters`}
                className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                style={{ aspectRatio: '3/2' }}
              >
                {b.image ? (
                  <img
                    src={b.image}
                    alt={b.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </Link>
              <div className="p-4 md:p-5">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                  <Link
                    to={`/tanach/books/${b.id}/chapters`}
                    className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    {b.name}
                  </Link>
                </h3>
              </div>
            </article>
          ))}
        </div>
      )}

      {!loading && !error && books.length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין ספרים להצגה.
        </p>
      )}
    </div>
  );
}